import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormGroup, Input } from 'reactstrap';

import IUserGet from 'api/interfaces/users/IUserGet';
import { WRITE_CONTACTS } from 'services/enums/users/permissions';

import customerApi from 'api/contacts';
import LinkButton from 'components/layout/buttons/LinkButton';
import SearchDropdown from 'components/layout/forms/SearchDropdown';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import TrashIcon from 'components/layout/icons/TrashIcon';
import translations from 'services/translations/translations';
import { showDesigners } from 'components/offers/OfferSelectOptions';

import NewProjectContactModal, {
  NEW_COMPANY,
  NEW_PERSON,
  NEW_PERSON_CHOICE,
} from './NewProjectContactModal';

import 'style/offers/ProjectContacts.css';

// The longest note the server accepts
export const PROCUREMENT_NOTE_MAX_LENGTH = 500;

// The contact type of a company created here
const NEW_COMPANY_TYPE = 'supplier';

export interface IProcurementContactRow {
  key: number;
  note: string;
  office: { label: string; value: string } | null;
  personId: string;
  // The contact persons of the company, null until loaded
  persons: any[] | null;
}

const loadPersons = async (officeId: string): Promise<any[]> => {
  try {
    const contact = await customerApi.getContact(officeId, {
      ownOrganizationUnit: true,
    });
    return contact.contactPersons ?? [];
  } catch (error) {
    return [];
  }
};

// Builds the rows from the saved procurement contacts; the persons are loaded by the rows
export const getProcurementContactRows = (
  contacts: {
    contact: { id: number; companyName: string };
    contactPerson: { id: number } | null;
    note?: string | null;
  }[]
): IProcurementContactRow[] =>
  contacts.map((procurementContact, index) => ({
    key: index + 1,
    note: procurementContact.note ?? '',
    office: {
      label: procurementContact.contact.companyName,
      value: procurementContact.contact.id.toString(),
    },
    personId: procurementContact.contactPerson?.id?.toString() ?? '',
    persons: null,
  }));

const emptyRow = (key: number): IProcurementContactRow => ({
  key,
  note: '',
  office: null,
  personId: '',
  persons: [],
});

// The rows with a company, as saved with the offer
export const getProcurementContactsPost = (
  rows: IProcurementContactRow[]
): { contactId: number; contactPersonId?: number; note?: string }[] =>
  rows
    .filter((row) => row.office !== null && row.office.value !== '')
    .map((row) => ({
      contactId: parseInt(row.office?.value ?? '', 10),
      contactPersonId:
        row.personId === '' ? undefined : parseInt(row.personId, 10),
      note: row.note.trim() === '' ? undefined : row.note.trim(),
    }));

/**
 * The procurement contacts of the offer (hankinnat): rows of a company, its contact person
 * and a short note, chosen like the customers. Shown in a section of the offer page that is
 * opened and closed from its title.
 */
const ProcurementContacts = ({
  locked,
  onSave,
  rows,
  searchOffice,
  setRows,
}: {
  locked: boolean;
  // Saves the rows at once (a company or person chosen, a row removed, a note left);
  // without it the rows are saved with the offer
  onSave?: (rows: IProcurementContactRow[]) => void;
  rows: IProcurementContactRow[];
  searchOffice: (inputValue: string) => Promise<any>;
  setRows: Dispatch<SetStateAction<IProcurementContactRow[]>>;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers, translations.common]);
  // A new company or contact person being created on a row
  const [newContact, setNewContact] = useState<null | {
    mode: string;
    rowKey: number;
  }>(null);
  const newContactRow =
    newContact === null
      ? undefined
      : rows.find((row) => row.key === newContact.rowKey);

  // Companies and persons can be created by the users who can edit the contacts
  const currentUser = useSelector(
    (state: { users: { current: IUserGet | null } }) => state.users.current
  );
  const canCreate =
    !locked &&
    (currentUser?.permissions?.some(
      (permission) => permission.type === WRITE_CONTACTS
    ) ??
      false);
  // The companies whose contact persons are being loaded, by row: "key:company id"
  const loading = useRef(new Set<string>());

  // The persons of each company are loaded in the background, and set only while the row
  // still has the same company
  useEffect(() => {
    rows
      .filter((row) => row.persons === null && row.office?.value)
      .forEach((row) => {
        const officeId = row.office?.value ?? '';
        const loadKey = `${row.key}:${officeId}`;

        if (loading.current.has(loadKey)) {
          return;
        }

        loading.current.add(loadKey);
        loadPersons(officeId).then((persons) => {
          loading.current.delete(loadKey);
          setRows((current) =>
            current.map((currentRow) =>
              currentRow.key === row.key &&
              currentRow.office?.value === officeId
                ? { ...currentRow, persons }
                : currentRow
            )
          );
        });
      });
  }, [rows, setRows]);

  // The ready empty row becomes a real one when something is chosen or written on it. A
  // finished change (a company or person chosen) is saved at once.
  const updateRow = (
    key: number,
    changes: Partial<IProcurementContactRow>,
    save = false
  ) => {
    const next = rows.some((row) => row.key === key)
      ? rows.map((row) => (row.key === key ? { ...row, ...changes } : row))
      : [...rows, { ...emptyRow(key), ...changes }];
    setRows(next);
    if (save) {
      onSave?.(next);
    }
  };

  const changeOffice = (
    key: number,
    office: { label: string; value: string } | null
  ) =>
    updateRow(
      key,
      {
        office,
        personId: '',
        persons: office !== null && office.value !== '' ? null : [],
      },
      true
    );

  // Below the ready empty row (which becomes a real one) when there are no rows yet
  const addRow = () =>
    setRows((current) => [
      ...(current.length === 0 ? [emptyRow(1)] : current),
      emptyRow(Math.max(1, ...current.map((row) => row.key)) + 1),
    ]);

  const removeRow = (key: number) => {
    const next = rows.filter((row) => row.key !== key);
    setRows(next);
    onSave?.(next);
  };

  // A company or a person created on a row is chosen on it
  const contactCreated = (created: { id: number; label: string }) => {
    if (newContact === null) {
      return;
    }

    if (newContact.mode === NEW_COMPANY) {
      changeOffice(newContact.rowKey, {
        label: created.label,
        value: created.id.toString(),
      });
    } else {
      // The persons of the company are loaded again with the new one
      updateRow(
        newContact.rowKey,
        {
          personId: created.id.toString(),
          persons: null,
        },
        true
      );
    }

    setNewContact(null);
  };

  // One empty row is always ready to be filled in
  const shownRows = rows.length === 0 && !locked ? [emptyRow(1)] : rows;

  // A new company goes to the last row while it has no company, otherwise to a new row
  // after it: never in place of a company already chosen
  const getNewCompanyRowKey = (row: IProcurementContactRow) =>
    row.office !== null && row.office.value !== ''
      ? Math.max(0, ...rows.map((existing) => existing.key)) + 1
      : row.key;

  return (
    <div className="project-contacts procurement-contacts">
      {shownRows.length === 0 ? (
        <p className="project-contacts-info">
          {t(`${translations.offers}:procurement.None`)}
        </p>
      ) : (
        <div className="project-contacts-grid project-contacts-header">
          <span>{t(`${translations.offers}:projectContacts.Company`)}</span>
          <span>{t(`${translations.offers}:projectContacts.Person`)}</span>
          <span>{t(`${translations.offers}:procurement.Phone`)}</span>
          <span>{t(`${translations.offers}:procurement.Email`)}</span>
          <span>{t(`${translations.offers}:procurement.Note`)}</span>
          <span />
        </div>
      )}
      {shownRows.map((row) => {
        // How the chosen person is reached, from the contacts
        const person = (row.persons ?? []).find(
          (candidate) => String(candidate.id) === row.personId
        );
        const phone = person?.phone || person?.phoneWithCountryCode || '';
        return (
          <div className="project-contacts-grid" key={row.key}>
            <FormGroup>
              <SearchDropdown
                value={row.office}
                placeholder={t(`${translations.common}:Search`)}
                className="text-field search-field"
                name={`procurement-contact-office-${row.key}`}
                onChange={(office: any) => changeOffice(row.key, office)}
                loadOptions={searchOffice}
                disabled={locked}
              />
            </FormGroup>
            <div>
              <SelectInputFieldControlled
                value={row.personId}
                id={`procurement-contact-person-${row.key}`}
                name={`procurement-contact-person-${row.key}`}
                onChange={(event: any) => {
                  const { value } = event.target;
                  // "+ Uusi henkilö" of the list: the window, the person stays as it was
                  if (value === NEW_PERSON_CHOICE) {
                    setNewContact({ mode: NEW_PERSON, rowKey: row.key });
                    return;
                  }
                  updateRow(row.key, { personId: value }, true);
                }}
                options={[
                  ...showDesigners(row.persons ?? []),
                  ...(canCreate &&
                  row.office !== null &&
                  row.office.value !== ''
                    ? [
                        <option
                          key={NEW_PERSON_CHOICE}
                          value={NEW_PERSON_CHOICE}
                        >
                          {`+ ${t(
                            `${translations.offers}:projectContacts.New person`
                          )}…`}
                        </option>,
                      ]
                    : []),
                ]}
                disabled={locked || row.office === null}
              />
            </div>
            <div className="procurement-contact-reach">
              {phone ? (
                <a href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a>
              ) : null}
            </div>
            <div className="procurement-contact-reach">
              {person?.email ? (
                <a href={`mailto:${person.email}`}>{person.email}</a>
              ) : null}
            </div>
            <Input
              className="text-field procurement-contact-note"
              id={`procurement-contact-note-${row.key}`}
              name={`procurement-contact-note-${row.key}`}
              maxLength={PROCUREMENT_NOTE_MAX_LENGTH}
              placeholder={t(
                `${translations.offers}:procurement.Note placeholder`
              )}
              value={row.note}
              onChange={(event) =>
                updateRow(row.key, { note: event.target.value })
              }
              // The note is saved when the field is left
              onBlur={() => onSave?.(rows)}
              disabled={locked}
            />
            <div>
              {locked || !rows.some((saved) => saved.key === row.key) ? null : (
                <TrashIcon
                  style={{ marginTop: '0.5rem' }}
                  onClick={() => removeRow(row.key)}
                />
              )}
            </div>
          </div>
        );
      })}
      {/* Below the rows, not between them: a supplier, then a new company under it */}
      {locked ? null : (
        <div className="procurement-contacts-actions">
          <LinkButton onClick={addRow}>
            {`+ ${t(`${translations.offers}:procurement.Add`)}`}
          </LinkButton>
          {canCreate && shownRows.length > 0 ? (
            <LinkButton
              onClick={() =>
                setNewContact({
                  mode: NEW_COMPANY,
                  rowKey: getNewCompanyRowKey(shownRows[shownRows.length - 1]),
                })
              }
            >
              {`+ ${t(`${translations.offers}:projectContacts.New company`)}`}
            </LinkButton>
          ) : null}
        </div>
      )}
      <NewProjectContactModal
        company={newContactRow?.office ?? null}
        mode={newContact?.mode ?? null}
        onCreated={contactCreated}
        organizationUnitId={currentUser?.organizationUnit?.id}
        role={NEW_COMPANY_TYPE}
        toggle={() => setNewContact(null)}
      />
    </div>
  );
};

export default ProcurementContacts;
